import "./Accordion.scss";
import * as React from "react";
import { Accordion as MuiAccordion, AccordionSummary as MuiAccordionSummary, AccordionDetails as MuiAccordionDetails } from "@mui/material";
import { Plus, Dash } from "./icons";
import { DocumentedComponent } from "./DocumentedComponent";
import clsx from "clsx";
import { getDataProps } from "../../shared/utilities";


interface AccordionProps {
    name?: string;
    expandedGroup?: string;
    fillAvailableSpace?: boolean;
    className?: string;
    children: Array<JSX.Element> | JSX.Element;
}

const Accordion: React.FunctionComponent<AccordionProps> = (props: AccordionProps) => {
    const [expanded, setExpanded] = React.useState<string | false>(props.expandedGroup);
    const setExpandedWithSupportForCollapsingFinalPanel = React.useCallback((panelName: string) => {
        if (expanded === panelName) {
            setExpanded("");
        } else {
            setExpanded(panelName);
        }
    }, [expanded]);

    const renderedChildren = React.Children.map(props.children, (child) =>
        React.cloneElement(child, { expanded, setExpanded: setExpandedWithSupportForCollapsingFinalPanel })
    );

    return <div className={clsx("accordion", props.fillAvailableSpace && "stretch-expanded-group", props.className)} {...getDataProps(props)}>
        {renderedChildren}
    </div>
};

(Accordion as DocumentedComponent).metadata = {
    description: "The `Accordion` component contains multiple `AccordionGroup` (Expansion Panel) components.  Only one Accordion Group can be expanded at a time.",
    attributes: [
        { name: "expandedGroup", type: "string", description: "Title of the `AccordionGroup` component that is expanded by default." },
        { name: "fillAvailableSpace", type: "boolean", description: "If `true`, the expanded group will stretch to fill the available space.  If `false`, the expanded group will only take up as much space as needed.  Optional - defaults to `false`." }
    ]
};


interface AccordionGroupProps {
    name?: string;
    title: string;
    children: any;
    expanded?: string;
    setExpanded?: (string) => void;
}

const AccordionGroup: React.FunctionComponent<AccordionGroupProps> = (props: AccordionGroupProps) => {
    const { title, children, expanded, setExpanded, ...other } = props;
    const isExpanded = expanded === title;
    const expandIcon = isExpanded ? <Dash /> : <Plus />;

    return <MuiAccordion expanded={isExpanded} onChange={() => setExpanded(title)} {...other} {...getDataProps(props)}  >
        <MuiAccordionSummary
            expandIcon={expandIcon}
            aria-controls={`${title}-content`}
            id={`${title}-header`}
        >
            {title}
        </MuiAccordionSummary>
        <MuiAccordionDetails>
            {children}
        </MuiAccordionDetails>
    </MuiAccordion>
};

(AccordionGroup as DocumentedComponent).metadata = {
    description: "An `AccordionGroup` consists of a header and a collapsible detail panel.  Collapsed/Expanded status of the detail panel is managed by the parent `Accordion`.",
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        { name: "title", type: "string", description: "Title shown in the accordion header.  Also used as the ID for the panel.  Must be unique across the AccordionGroup." }
    ]
};


export { Accordion, AccordionGroup };
export type { AccordionGroupProps, AccordionProps };
